import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '../lib/api.js';

const STATUS_META = {
  proposed: { label: 'Proposed', cls: 'bg-cream-100 text-ink-700' },
  active:   { label: 'Active',   cls: 'bg-olive-100 text-olive-900' },
  paused:   { label: 'Paused',   cls: 'bg-gold-500/15 text-gold-600' },
  done:     { label: 'Done',     cls: 'bg-ink-700/10 text-ink-700' },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner } = useOutletContext();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  function load() { api.get(`/api/projects/${id}`).then(setData).catch(e => setErr(e.message)); }
  useEffect(() => { load(); }, [id]);

  async function toggleHidden() {
    const next = data.project.status === 'done' ? 'active' : 'done';
    try { await api.patch(`/api/projects/${id}`, { status: next }); load(); }
    catch (e) { setErr(e.message); }
  }
  async function deleteProject() {
    const p = data.project;
    if (!confirm(
      `Delete project "${p.name}"?\n\n` +
      `• Income / expense rows tagged to this project will stay, but un-tagged.\n` +
      `• USDA programs that referenced this project as the related project will be unlinked.\n` +
      `• Marking the project Done (Hide) is usually enough to declutter.\n\n` +
      `Type "delete" in the next prompt to confirm.`,
    )) return;
    if (prompt(`Confirm: type "delete" to remove project "${p.name}"`) !== 'delete') return;
    try {
      const r = await api.delete(`/api/projects/${id}`);
      const u = r.untagged || {};
      alert(`✓ Deleted. Un-tagged ${u.income || 0} income, ${u.expense_lines || 0} expense lines, unlinked ${u.usda_programs || 0} USDA program(s).`);
      navigate('/projects');
    } catch (e) { setErr(e.message); }
  }

  if (err) return <div className="text-rust-700 text-sm">{err}</div>;
  if (!data) return <div className="text-ink-700/60 italic">Loading…</div>;
  const p = data.project;
  const meta = STATUS_META[p.status] || STATUS_META.proposed;

  const spent = data.totals?.spent_cents || 0;
  const reimbursed = data.totals?.reimbursed_cents || 0;
  const net = isOwner ? spent - reimbursed : null;
  const remaining = isOwner && p.budget_cents != null ? p.budget_cents - net : null;
  const pct = isOwner && p.budget_cents ? Math.round((spent / p.budget_cents) * 100) : null;

  return (
    <div className="space-y-6">
      <Link to="/projects" className="text-sm text-olive-700 hover:underline">‹ Projects</Link>

      <header className="card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 flex items-start gap-3">
            <img src="/logo.svg" alt="" className="h-10 w-10 mt-1" />
            <div className="flex-1">
              <h1 className="text-2xl font-serif text-olive-900">{p.name}</h1>
              <div className="text-sm text-ink-700/70 mt-1 flex items-center gap-2 flex-wrap">
                <span className={`pill ${meta.cls}`}>{meta.label}</span>
                {p.segment_name && <span>· {p.segment_name}</span>}
                {p.owner_name && <span>· Owner: <strong>{p.owner_name}</strong></span>}
                {p.deadline && <span>· Due {fmtDate(p.deadline)}</span>}
              </div>
            </div>
          </div>
          {isOwner && (
            <div className="flex gap-2 text-sm flex-wrap">
              <Link to={`/projects/${p.id}/edit`} className="btn-ghost text-sm">Edit</Link>
              <button onClick={toggleHidden} className="btn-ghost text-xs py-1 px-2">
                {p.status === 'done' ? 'Reopen' : 'Mark Done'}
              </button>
              <button onClick={deleteProject} className="text-xs text-rust-700 hover:underline px-2">
                Delete
              </button>
            </div>
          )}
        </div>
        {p.description && <p className="mt-3 text-ink-700">{p.description}</p>}
        {p.notes && <p className="mt-2 text-sm text-ink-700/80 italic">{p.notes}</p>}
      </header>

      {isOwner && p.budget_cents != null && (
        <section className="card">
          <h2 className="font-medium text-olive-900 mb-3">Budget vs. actual</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <Field label="Budget"><strong>{fmtMoney(p.budget_cents)}</strong></Field>
            <Field label="Spent" tone="rust">{fmtMoney(spent)}</Field>
            <Field label="USDA reimbursed" tone="olive">{fmtMoney(reimbursed)}</Field>
            <Field label="Net to farm" tone={net > p.budget_cents ? 'rust' : 'olive'}>
              <strong>{fmtMoney(net)}</strong>
            </Field>
          </div>
          {pct != null && (
            <div className="mt-3">
              <div className="text-xs text-ink-700/70">{pct}% of budget spent</div>
              <div className="mt-1 h-2 bg-cream-100 rounded-full overflow-hidden">
                <div className={`h-full ${pct > 100 ? 'bg-rust-500' : 'bg-olive-700'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
            </div>
          )}
        </section>
      )}

      {data.usda_programs.length > 0 && (
        <section className="card">
          <h2 className="font-medium text-olive-900 mb-2">USDA programs</h2>
          <ul className="space-y-1 text-sm">
            {data.usda_programs.map(u => (
              <li key={u.id} className="flex items-center gap-3">
                <span className="pill bg-rust-500/15 text-rust-700">{u.status}</span>
                <span className="font-medium">{u.program_name}</span>
                {isOwner && u.expected_amount_cents != null && (
                  <span className="text-ink-700/70 ml-auto text-xs">
                    expected {fmtMoney(u.expected_amount_cents)}
                    {u.received_amount_cents > 0 && <> · received {fmtMoney(u.received_amount_cents)}</>}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {isOwner && data.expenses.length > 0 && (
        <section className="card">
          <h2 className="font-medium text-olive-900 mb-2">Expense lines tagged to this project ({data.expenses.length})</h2>
          <ul className="space-y-1 text-sm">
            {data.expenses.map(e => (
              <li key={e.id} className="flex justify-between border-l-2 border-olive-100 pl-3 py-1">
                <span>
                  <span className="text-ink-900">{e.description || e.vendor}</span>
                  <span className="text-ink-700/60 ml-2">{fmtDate(e.date)}</span>
                  {e.schedule_f_code && <span className="ml-2 text-xs text-ink-700/50">{e.schedule_f_code}</span>}
                </span>
                <strong className="text-rust-700">{fmtMoney(e.line_total_cents)}</strong>
              </li>
            ))}
          </ul>
        </section>
      )}

      {isOwner && data.income.length > 0 && (
        <section className="card">
          <h2 className="font-medium text-olive-900 mb-2">Income tagged to this project ({data.income.length})</h2>
          <ul className="space-y-1 text-sm">
            {data.income.map(i => (
              <li key={i.id} className="flex justify-between border-l-2 border-olive-100 pl-3 py-1">
                <span>
                  <span className="text-ink-900">{i.description || i.customer_name}</span>
                  <span className="text-ink-700/60 ml-2">{fmtDate(i.date)}</span>
                  {i.schedule_f_code && <span className="ml-2 text-xs text-ink-700/50">{i.schedule_f_code}</span>}
                </span>
                <strong className="text-olive-700">{fmtMoney(i.paid_cents)}</strong>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Field({ label, children, tone }) {
  const c = tone === 'rust' ? 'text-rust-700' : tone === 'olive' ? 'text-olive-900' : 'text-ink-900';
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-ink-700/60">{label}</dt>
      <dd className={`mt-0.5 ${c}`}>{children}</dd>
    </div>
  );
}
